import React, { useState, useCallback, useRef } from "react";
import {
  challenge as challengeApi,
  completeLogin as completeLoginApi,
  loginWithCredentials as loginWithCredentialsApi,
  refreshQrCode,
  beginLoginFlow as apiBeginLoginFlow,
} from "@/api";
import useDevice from "@/hooks/useDevice";
import Mixpanel from "@/services/tracking";

const POLL_RATE_IN_MS = 1000;

const getTimeUntilNextRequest = (requestTime: number) => {
  return POLL_RATE_IN_MS - requestTime > 0 ? POLL_RATE_IN_MS - requestTime : 0;
};

type LoginState =
  | "idle"
  | "enter_identifier"
  | "waiting_for_bankid"
  | "starting"
  | "polling"
  | "user_signing"
  | "authenticated"
  | "error";

type ContextValue = {
  session?: LoginResponse;
  challenge(identifier: string): Promise<ChallengeResponse>;
  completeLogin(refToken: string): Promise<void>;
  loginWithCredentials(identifier: string): Promise<void>;
  loginOnDevice(): Promise<void>;
  logout(): void;
  beginQrLoginFlow(): Promise<void>;
  cancelLogin: () => void;
  setLoginState: (newState: LoginState) => void;
  loginState: LoginState;
  activeQrCode?: Base64SvgImage;
  error?: unknown;
};

export const AuthContext = React.createContext<ContextValue | undefined>(
  undefined
);

export const AuthProvider = ({ children }: React.PropsWithChildren) => {
  const [session, setSessionRaw] = useState<LoginResponse | undefined>();
  const [activeQrCode, setActiveQrCode] = useState<Base64SvgImage>();
  const [loginState, _setLoginState] = useState<LoginState>("idle");
  const loginStateRef = useRef<LoginState>("idle");
  const [error, _setError] = useState<unknown>();
  const timeoutRef = useRef<number>();
  const abortControllerRef = useRef<AbortController>();
  const device = useDevice();

  const beginQrLoginFlow = async () => {
    setLoginState("starting");

    try {
      const start = new Date();
      const response = await apiBeginLoginFlow();
      const finish = new Date();
      const requestDuration = finish.valueOf() - start.valueOf();
      setActiveQrCode(window.atob(response.QRCode));
      setLoginState("polling");
      timeoutRef.current = setTimeout(() => {
        void updateQrCode(response.sessionId);
      }, getTimeUntilNextRequest(requestDuration));
    } catch (err) {
      setError(err);
      console.error(err);
    }
  };

  const updateQrCode = async (sessionId: string) => {
    try {
      abortControllerRef.current = new AbortController();
      const start = new Date();
      const response = await refreshQrCode(
        sessionId,
        abortControllerRef.current.signal
      );
      const finish = new Date();
      const requestDuration = finish.valueOf() - start.valueOf();
      const status = response.sessionResponse?.grandidObject?.message?.status;
      const hintCode =
        response.sessionResponse?.grandidObject?.message?.hintCode;

      if (hintCode === "userSign") {
        setLoginState("user_signing");
      }

      if (status === "failed") {
        if (hintCode === "userCancel") {
          setLoginState("idle");
        } else {
          setError("Login failed");
        }
      }

      if (status === "succeeded" && response.loginResponse) {
        setSession(response.loginResponse as LoginResponse);
        setLoginState("authenticated");
        return;
      }

      setActiveQrCode(
        window.atob(response.sessionResponse.grandidObject.QRCode)
      );

      if (["polling", "user_signing"].includes(loginStateRef.current)) {
        timeoutRef.current = setTimeout(() => {
          void updateQrCode(sessionId);
        }, getTimeUntilNextRequest(requestDuration));
      }
    } catch (err) {
      setError(err);
      console.error(err);
      clearTimeout(timeoutRef.current);
    }
  };

  const cancelLogin = () => {
    setLoginState("idle");
    setActiveQrCode(undefined);
    clearError();
    clearTimeout(timeoutRef.current);
    abortControllerRef.current?.abort("Login canceled");
  };

  const setError = useCallback((err: unknown) => {
    _setError(err);
    console.error(err);
    setLoginState("error");
  }, []);

  const clearError = () => {
    _setError(undefined);
    setLoginState("idle");
  };

  const setLoginState = (newState: LoginState) => {
    _setLoginState(newState);
    loginStateRef.current = newState;
  };

  const getBankIdUrl = (payload: string) => {
    return payload.replace(
      "bankid:///",
      device.isAppleDevice ? "https://app.bankid.com/" : "bankid:///"
    );
  };

  const loginOnDevice = async () => {
    try {
      setLoginState("waiting_for_bankid");

      const challengeResponse = await challenge();

      const newUrl = getBankIdUrl(challengeResponse.payload);
      console.log("Attempting to open BankID");
      Mixpanel.track("OpenBankID");
      window.location.replace(newUrl);
    } catch (e) {
      setError(e);
      console.error(e);
    }
  };

  const challenge = async (): Promise<ChallengeResponse> => {
    const challengeResponse = await challengeApi();

    if (!challengeResponse.ok) {
      throw new Error("Challenge failed");
    }

    const challengeJson: ChallengeResponse =
      (await challengeResponse.json()) as ChallengeResponse;

    localStorage.setItem("refToken", challengeJson.refToken);

    if (!challengeJson.refToken) {
      throw new Error("RefToken missing");
    }

    return challengeJson;
  };

  const completeLogin = useCallback(
    async (refToken: string) => {
      const loginResponse = await completeLoginApi(refToken);

      localStorage.removeItem("refToken");

      if (!loginResponse.ok) {
        const error = new Error();
        if (loginResponse.status === 409) {
          error.message = "Conflict";
        }

        const json = await loginResponse.json();
        console.log("loginResponse error", JSON.stringify(json));

        setError("Login failed");
        throw error;
      }

      const loginResponseJson: LoginResponse =
        (await loginResponse.json()) as LoginResponse;

      setSession(loginResponseJson);
      setLoginState("authenticated");
    },
    [setError]
  );

  const loginWithCredentials = async (identifier: string) => {
    setLoginState("starting");

    const credentialsResponse = await loginWithCredentialsApi(identifier);

    if (!credentialsResponse.ok) {
      const error = new Error();
      if (credentialsResponse.status === 409) {
        error.message = "Conflict";
      }

      setError("Credentials login failed");
      throw error;
    }

    const loginResponseJson: LoginResponse =
      (await credentialsResponse.json()) as LoginResponse;

    setSession(loginResponseJson);
  };

  const setSession = (loginResponse: LoginResponse) => {
    setSessionRaw(loginResponse);
  };

  const logout = () => {
    setSessionRaw(undefined);
  };

  const value = {
    session,
    loginState,
    activeQrCode,
    error,
    challenge,
    completeLogin,
    loginWithCredentials,
    loginOnDevice,
    logout,
    beginQrLoginFlow,
    cancelLogin,
    setLoginState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
